import { Badge } from "@component/badge";
import { Button } from "@component/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@component/card";
import Pagination from "@component/pagination";
import { PDFViewer } from "@component/pdf-viewer";
import { useSubscription } from "@context/subscription_context";
import { useUserProfileContext } from "@context/user_profile";
import { useSnackbar } from "@hook/use-snackbar";
import { getDetailInvoice, getListInvoices } from "@service/invoice";
import { InvoiceDetail, InvoiceItem } from "@type/billing";
import { MetaPagination } from "@type/common";
import { parseFormat } from "@util/date";
import { catchErrorLog } from "@util/sentry";
import { DownloadIcon, EyeIcon, FileTextIcon, InfoIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";

interface BillingInvoiceProps {
    /** Opens the "Get in Touch" dialog shown in the trial notice. Optional —
     *  omitted on surfaces without that dialog (e.g. the Subscription page),
     *  where the button is simply hidden. */
    setShowRequestInfoDialog?: (value: boolean) => void;
}

export default function BillingInvoice({ setShowRequestInfoDialog }: BillingInvoiceProps) {
    const snackbar = useSnackbar();
    const { subscriptionActive } = useSubscription();
    const [, userInfo] = useUserProfileContext();
    const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetail | null>(null);
    const [metaPagination, setMetaPagination] = useState<MetaPagination>({
        limit: 0,
        page: 1,
        total_pages: 0,
        total: 0,
    })

    useEffect(() => {
        setIsLoading(true);
        getListInvoices({
            page: currentPage,
            limit: 20,
        }).then((response) => {
            setInvoices(response.data.data);
            setMetaPagination(response.data.meta);
        }).catch((error) => {
            catchErrorLog(`Failed to fetch invoices for page ${currentPage}: ${error}`);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [currentPage])

    const onLoadPagination = (page: number) => {
        setCurrentPage(page);
    }

    const onViewInvoiceDetail = (invoice_id: string) => {
        getDetailInvoice(invoice_id).then((resp) => {
            if (resp.status === 200) {
                setInvoiceDetail(resp.data.data);
            }
        }).catch((err) => {
            catchErrorLog(`failed to get invoice detail, err: ${err}`)
            snackbar.showError(`Failed to load invoice details.`)
        })
    }

    const handleDownloadInvoice = async () => {
        if (!invoiceDetail?.invoice_url || !invoiceDetail?.filename) return;

        try {
            const response = await fetch(invoiceDetail.invoice_url);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // FE-generated download name: "ourteam Invoice_<number>_<company>_<DMMMMYYYY>.pdf"
            const invNum = (invoiceDetail.invoice_number ?? "").replace(/^INV-?/i, "");
            const invoiceItem = invoices.find(
                (i) => i.invoice_number === invoiceDetail.invoice_number,
            );
            const dateStr = invoiceItem?.created_at
                ? parseFormat(invoiceItem.created_at, "DMMMMYYYY")
                : "";
            const company = (userInfo?.company_name ?? "").replace(/[\\/:*?"<>|]/g, "").trim();
            const nameParts = ["ourteam Invoice", invNum, company, dateStr].filter(Boolean);
            link.download = `${nameParts.join("_")}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download Invoice:', error);
            // Fallback to opening in new tab if download fails
            window.open(invoiceDetail.invoice_url, "_blank");
        }
    };

    return (
        <div className="flex gap-6 relative">
            <div className={`transition-all duration-300 ${invoiceDetail ? "flex-1" : "w-full"}`}>
                <Card>
                    <CardHeader className="space-y-1">
                        <div className="flex items-center justify-between gap-4">
                            <div className="space-y-1">
                                <CardTitle>Your Invoices</CardTitle>
                                <CardDescription>
                                    Download invoices for your previous billing cycles.
                                </CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                                {(invoices.length > 0) && (
                                    <Badge className="bg-blue-100 hover:bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-400 rounded-full shadow-none">
                                        {invoices.length}{" "}
                                        {invoices.length === 1 ? "invoice" : "invoices"}
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {subscriptionActive?.is_trial && (
                            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-3">
                                <InfoIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                    You're on a trial workspace plan; no invoices will be
                                    generated for your usage.
                                    {setShowRequestInfoDialog && (
                                        <>
                                            {" "}
                                            <button
                                                onClick={() => setShowRequestInfoDialog(true)}
                                                className="text-sm font-medium text-blue-900 dark:text-blue-100 underline hover:text-foreground dark:hover:text-foreground transition-colors"
                                            >
                                                Get in Touch
                                            </button>
                                        </>
                                    )}
                                </p>
                            </div>
                        )}
                        {(invoices.length === 0) ? (
                            /* Empty State */
                            <div className="flex flex-col items-center justify-center py-16 px-6">
                                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                    <FileTextIcon className="h-8 w-8 text-muted-foreground" />
                                </div>
                                <p className="text-center text-muted-foreground font-medium mb-2">
                                    No invoices available.
                                </p>
                                <p className="text-center text-sm text-muted-foreground/80">
                                    Your invoices will appear here once your billing cycle
                                    begins.
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Scrollable Table Container */}
                                <div className="max-h-[500px] overflow-y-auto border rounded-lg">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="sticky top-0 bg-muted/50 z-10">
                                                <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Invoice Number
                                                </th>
                                                <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                                                <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                                                <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoices.map((invoice, key) => (
                                                <tr key={key}>
                                                    <td className="px-4 py-4 whitespace-nowrap text-left text-sm font-medium">
                                                        {invoice.invoice_number}
                                                    </td>
                                                    <td className="px-4 py-4 whitespace-nowrap text-left text-sm font-medium">
                                                        {parseFormat(invoice.created_at ?? "", "D MMMM YYYY")}
                                                    </td>
                                                    <td className="px-4 py-4 whitespace-nowrap text-left text-sm font-medium">
                                                        {invoice.currency} {invoice.amount}
                                                    </td>
                                                    <td className="px-4 py-4 whitespace-nowrap text-center">
                                                        <div className="flex items-center gap-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() =>
                                                                    onViewInvoiceDetail(invoice.invoice_id ?? "")
                                                                }
                                                            >
                                                                <EyeIcon className="h-4 w-4" />
                                                                View Invoice
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="p-4">
                                    <Pagination
                                        isLoading={isLoading}
                                        currentPage={currentPage}
                                        totalPages={metaPagination.total_pages || 0}
                                        onPageChange={onLoadPagination}
                                    />
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Invoice Viewer Drawer */}
            {invoiceDetail && (
                <div className="w-1/2 flex-shrink-0 transition-all duration-300">
                    <Card className="h-full">
                        <CardHeader className="border-b border-border">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="text-lg">
                                        Invoice Preview
                                    </CardTitle>
                                    <CardDescription className="text-sm mt-1">
                                        {invoiceDetail.invoice_number}
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button onClick={handleDownloadInvoice} variant="outline" size="sm" className="h-8">
                                        <DownloadIcon className="h-4 w-4 mr-2" />
                                        Download Invoice
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setInvoiceDetail(null)}
                                        className="h-8 w-8 p-0"
                                    >
                                        <XIcon className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0">
                            {/* PDF Viewer Placeholder */}
                            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg h-[600px]">
                                <PDFViewer
                                    url={invoiceDetail.invoice_url || ""}
                                    defaultZoom={1}
                                    className="w-full h-full"
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    )
}